import { useEffect, useRef, useState, type ChangeEvent, type FormEvent, type ReactNode } from 'react';
import { ArrowRight, CheckCircle2, RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { publicApi, publicErrorMessage } from '../services/publicApi';

const SIZES = ['Clinic (no beds)', '1–30 beds', '31–100 beds', '101–300 beds', 'More than 300 beds'];
type Captcha = Awaited<ReturnType<typeof publicApi.captcha>>;

// Requests a workspace through the public backend; no CRM credentials or staff token are sent.
export default function SignupRequestForm({ fallback }: { fallback: ReactNode }) {
  const { t } = useTranslation();
  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [form, setForm] = useState({ hospital: '', name: '', email: '', phone: '', city: '', size: '', message: '', _gotcha: '' });
  const [state, setState] = useState<'idle' | 'sending' | 'sent'>('idle');
  const [error, setError] = useState<string | null>(null);
  const [captcha, setCaptcha] = useState<Captcha | null>(null);
  const [answer, setAnswer] = useState('');
  const [captchaLoading, setCaptchaLoading] = useState(false);
  const [captchaFailed, setCaptchaFailed] = useState(false);
  const busy = useRef(false);

  useEffect(() => {
    let active = true;
    publicApi.signupRequestsEnabled().then(async available => {
      if (!active) return;
      setEnabled(available);
      if (!available) return;
      setCaptchaLoading(true);
      try {
        const challenge = await publicApi.captcha();
        if (active) setCaptcha(challenge);
      } catch {
        if (active) setCaptchaFailed(true);
      } finally {
        if (active) setCaptchaLoading(false);
      }
    }).catch(() => { if (active) setEnabled(false); });
    return () => { active = false; };
  }, []);

  const refreshCaptcha = async () => {
    setCaptcha(null);
    setAnswer('');
    setCaptchaFailed(false);
    setCaptchaLoading(true);
    try {
      setCaptcha(await publicApi.captcha());
    } catch {
      setCaptchaFailed(true);
    } finally {
      setCaptchaLoading(false);
    }
  };

  const set = (field: keyof typeof form) => (event: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setForm(previous => ({ ...previous, [field]: event.target.value }));
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (busy.current || !captcha || captchaLoading || !answer.trim()) return;
    busy.current = true;
    setState('sending');
    setError(null);
    try {
      await publicApi.sendSignupRequest({
        ...form,
        hospital: form.hospital.trim(), name: form.name.trim(),
        email: form.email.trim(), phone: form.phone.trim(),
        captcha_token: captcha.token, captcha_answer: answer.trim(),
      });
      setState('sent');
    } catch (err) {
      setError(publicErrorMessage(err, t('signup.sendFailed', 'Your request could not be confirmed. Please try again or email sales@driventra.com.')));
      // The server consumes a challenge even when its answer is wrong or CRM forwarding fails.
      await refreshCaptcha();
      setState('idle');
    } finally {
      busy.current = false;
    }
  };

  if (enabled === null) return <p role="status" className="hms-auth-help">{t('signup.loading', 'Loading signup options…')}</p>;
  if (!enabled) return <>{fallback}</>;
  if (state === 'sent') return (
    <div className="hms-auth-onboarding" role="status">
      <h3><CheckCircle2 size={18} className="inline -mt-1 mr-1" /> {t('signup.received', 'Request received')}</h3>
      <p>{t('signup.receivedMessage', 'Thank you. The DaAFYA team will contact you at {{email}} to plan your workspace. No account has been created yet.', { email: form.email })}</p>
    </div>
  );

  const sending = state === 'sending';
  return (
    <form onSubmit={submit} className="hms-auth-form" aria-busy={sending}>
      <div><label htmlFor="su-hospital">{t('signup.hospital', 'Hospital or clinic name')}</label><input id="su-hospital" required pattern=".*\S.*" maxLength={200} autoComplete="organization" value={form.hospital} onChange={set('hospital')} disabled={sending} /></div>
      <div><label htmlFor="su-name">{t('signup.name', 'Your name')}</label><input id="su-name" required pattern=".*\S.*" maxLength={200} autoComplete="name" value={form.name} onChange={set('name')} disabled={sending} /></div>
      <div><label htmlFor="su-email">{t('signup.email', 'Work email')}</label><input id="su-email" type="email" required maxLength={254} autoComplete="email" value={form.email} onChange={set('email')} disabled={sending} /></div>
      <div><label htmlFor="su-phone">{t('signup.phone', 'Phone')}</label><input id="su-phone" type="tel" required pattern=".*\S.*" maxLength={64} autoComplete="tel" value={form.phone} onChange={set('phone')} disabled={sending} /></div>
      <div><label htmlFor="su-city">{t('signup.city', 'City')}</label><input id="su-city" maxLength={120} autoComplete="address-level2" value={form.city} onChange={set('city')} disabled={sending} /></div>
      <div><label htmlFor="su-size">{t('signup.size', 'Size')}</label><select id="su-size" value={form.size} onChange={set('size')} disabled={sending}><option value="">{t('signup.selectSize', 'Select…')}</option>{SIZES.map((size, index) => <option key={size} value={size}>{t(`signup.sizes.${index}`, size)}</option>)}</select></div>
      <div><label htmlFor="su-message">{t('signup.message', 'Locations and departments (optional)')}</label><textarea id="su-message" rows={3} maxLength={5000} value={form.message} onChange={set('message')} disabled={sending} /></div>
      <input type="text" name="_gotcha" tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden" value={form._gotcha} onChange={set('_gotcha')} />
      <div>
        <label htmlFor="su-captcha">{t('signup.captcha', 'Characters in the picture')}</label>
        <div className="hms-auth-captcha">
          {captcha && <img src={`data:image/svg+xml;charset=utf-8,${encodeURIComponent(captcha.svg)}`} alt={t('signup.captchaImage', 'Verification characters')} />}
          <button type="button" onClick={() => void refreshCaptcha()} disabled={sending || captchaLoading} aria-label={t('signup.refreshCaptcha', 'Load a new picture')}><RefreshCw size={18} className={captchaLoading ? 'animate-spin' : ''} /></button>
          <input id="su-captcha" required maxLength={10} autoComplete="off" autoCapitalize="characters" spellCheck={false} value={answer} onChange={event => setAnswer(event.target.value)} disabled={sending || captchaLoading || !captcha} />
        </div>
        {captchaLoading && <p role="status" className="hms-auth-help">{t('signup.loadingCaptcha', 'Loading verification picture…')}</p>}
        {captchaFailed && <p role="alert" className="hms-auth-error">{t('signup.captchaFailed', 'The verification picture could not be loaded. Use the refresh button to try again.')}</p>}
      </div>
      {error && <p role="alert" className="hms-auth-error">{error}</p>}
      <button className="hms-auth-submit" type="submit" disabled={sending || captchaLoading || !captcha || !answer.trim()}>{sending ? t('signup.sending', 'Sending…') : t('signup.submit', 'Request hospital onboarding')}<ArrowRight size={18} /></button>
      <p className="hms-auth-help">{t('signup.help', 'Your details go to the DaAFYA team at Driventra to set up your workspace. An account is not created yet.')}</p>
      <a className="hms-auth-help" href="mailto:sales@driventra.com?subject=DaAFYA%20hospital%20onboarding">{t('signup.emailInstead', 'Prefer email? Contact sales@driventra.com')}</a>
    </form>
  );
}
